/* eslint-disable react-hooks/rules-of-hooks */
import React from "react";
import {
  Title,
  useGetOne,
  ListBase,
  Datagrid, NumberField,
  TopToolbar,
  FunctionField
} from "react-admin";
import { useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button as MUIButton,
  Stack,
  Chip,
  IconButton,
} from "@mui/material";
import { EquipmentCreateDialog } from "../equipments/EquipmentCreateDialog";
import { EquipmentEditDialog } from "../equipments/EquipmentEditDialog";
import { WastesChoisesLabels } from "../common/consts/wasteList.const";

import { Edit } from "@mui/icons-material";
import { SiteHeader } from "./SiteHeader";
import { formatEquipmentLabel } from "../utils/formatEquipment";
import { formatPlanSummary, FrequencyLabels } from "../utils/formatPlanSummary";

export const SitePreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: site, isLoading } = useGetOne("sites", { id: id! });

  const [openCreate, setOpenCreate] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [editId, setEditId] = React.useState<string | null>(null);

  if (isLoading) return <Typography>Chargement…</Typography>;
  if (!site) return <Typography>Site introuvable.</Typography>;

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Aperçu du site" />

      <SiteHeader site={site} />

      <Paper sx={{ p: 2 }}>
        <Grid container justifyContent="space-between" sx={{ mb: 1 }}>
          <Grid>
            <Typography variant="h6" component="h2">
              Équipements sur le site
            </Typography>
          </Grid>
          <Grid>
            <TopToolbar>
              <MUIButton
                variant="contained"
                onClick={() => setOpenCreate(true)}
              >
                Ajouter un équipement
              </MUIButton>
            </TopToolbar>
          </Grid>
        </Grid>

        <ListBase resource="equipments" filter={{ site: id }} perPage={25}>
          <Datagrid rowClick={false}>
            <FunctionField
              label="Nom"
              render={(eq: any) => (eq ? formatEquipmentLabel(eq) : "-")}
            />
            <FunctionField
              label="Déchet"
              render={(record: any) => {
                const label = WastesChoisesLabels[record.waste] || record.waste;
                return (
                  label && (
                    <Chip
                      key={record.waste}
                      label={label}
                      size="small"
                      color="info"
                    />
                  )
                );
              }}
            />
            <NumberField source="density" label="Densité" textAlign="center" />
            <NumberField
              source="quantity"
              label="Quantité"
              textAlign="center"
            />
            <FunctionField
              label="Fréquence"
              sortable={false}
              render={(record: any) => {
                const freq = record?.collectionPlan?.frequency;
                if (!freq) return "—";
                return (
                  <Chip
                    size="small"
                    label={FrequencyLabels[freq] ?? freq}
                    color="secondary"
                    variant="outlined"
                  />
                );
              }}
            />
            <FunctionField
              label="Plan collecte"
              sortable={false}
              render={(record: any) => {
                const cp = record.collectionPlan;
                if (!cp) return "—";
                return (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Typography variant="body2" component="span">
                      {formatPlanSummary(cp)}
                    </Typography>
                  </Stack>
                );
              }}
            />
            <FunctionField
              label="Active"
              textAlign="center"
              render={(record) =>
                record.active ? (
                  <Chip label="Oui" color="success" size="small" />
                ) : (
                  <Chip label="Non" color="default" size="small" />
                )
              }
            />
            <FunctionField
              source="id"
              label="Actions"
              sortable={false}
              render={(record: any) => (
                <>
                  <IconButton
                    aria-label="modifier"
                    color="warning"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditId(record.id);
                      setOpenEdit(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </>
              )}
            />
          </Datagrid>
        </ListBase>
      </Paper>

      <EquipmentCreateDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        siteId={id!}
      />
      <EquipmentEditDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        id={editId}
      />
    </Box>
  );
};
